import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  Filter,
  Loader2,
  MessageSquare,
  Mic,
  Search,
  TrendingUp,
  UserCheck,
  UserMinus,
  MessageSquareWarning,
} from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import AgentPageShell from '../../components/agent/AgentPageShell';
import { ResolutionBadge, FeedbackBadge } from '../../components/agent/StatusBadge';
import { fetchSessionStats, fetchSessions } from '../../lib/agentApi';
import { formatWhen } from './AgentDashboardPage';

const ChatLogsPage = () => (
  <AgentPageShell
    title="Central Chat Logs"
    description="Every end-user conversation, searchable and filterable"
    icon={FileText}
    breadcrumb={['Chat Logs']}
  >
    {({ agent, orgId }) => <LogsBody key={agent.id} agent={agent} orgId={orgId} />}
  </AgentPageShell>
);

const LogsBody = ({ agent, orgId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [filters, setFilters] = useState({
    resolution_state: 'all',
    feedback: 'all',
    channel: 'all',
    language: 'all',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sessions, statsResult] = await Promise.all([
        fetchSessions(orgId, agent.id, {
          ...filters,
          q: applied || undefined,
          page,
          page_size: pageSize,
        }),
        fetchSessionStats(orgId, agent.id, 30),
      ]);
      setData(sessions);
      setStats(statsResult);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load chat logs');
    } finally {
      setLoading(false);
    }
  }, [orgId, agent.id, filters, applied, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  };

  const chartData = stats
    ? [
        { name: 'Resolved by AI', value: stats.resolved_by_ai, color: 'hsl(var(--chart-2))' },
        { name: 'Escalated', value: stats.escalated, color: 'hsl(var(--chart-3))' },
        { name: 'Negative feedback', value: stats.negative_feedback, color: 'hsl(var(--chart-5))' },
      ].filter((d) => d.value > 0)
    : [];

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Total sessions"
            value={stats.total_sessions.toLocaleString()}
            sub="Last 30 days"
            icon={TrendingUp}
            tone="text-brand-blue"
          />
          <StatCard
            label="Resolved by AI"
            value={stats.resolved_by_ai.toLocaleString()}
            percent={`${stats.resolution_rate}%`}
            icon={UserCheck}
            tone="text-emerald-500"
          />
          <StatCard
            label="Escalated"
            value={stats.escalated.toLocaleString()}
            percent={`${stats.escalation_rate}%`}
            icon={UserMinus}
            tone="text-amber-500"
          />
          <StatCard
            label="Negative feedback"
            value={stats.negative_feedback.toLocaleString()}
            percent={`${stats.negative_rate}%`}
            icon={MessageSquareWarning}
            tone="text-rose-500"
          />
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by message, session id or user…"
                className="pl-10 h-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setApplied(search);
                    setPage(1);
                  }
                }}
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <FilterSelect
                value={filters.resolution_state}
                onChange={(v) => setFilter('resolution_state', v)}
                width="w-[160px]"
                options={[
                  ['all', 'All statuses'],
                  ['ai_resolved', 'Resolved by AI'],
                  ['escalated', 'Escalated'],
                  ['pending', 'Pending'],
                  ['abandoned', 'Abandoned'],
                ]}
              />
              <FilterSelect
                value={filters.feedback}
                onChange={(v) => setFilter('feedback', v)}
                width="w-[150px]"
                options={[
                  ['all', 'All feedback'],
                  ['positive', 'Positive'],
                  ['negative', 'Negative'],
                  ['none', 'No feedback'],
                ]}
              />
              <FilterSelect
                value={filters.channel}
                onChange={(v) => setFilter('channel', v)}
                width="w-[130px]"
                options={[
                  ['all', 'All channels'],
                  ['voice', 'Voice'],
                  ['text', 'Text'],
                ]}
              />
              <FilterSelect
                value={filters.language}
                onChange={(v) => setFilter('language', v)}
                width="w-[140px]"
                options={[
                  ['all', 'All languages'],
                  ['ar', 'Arabic'],
                  ['en', 'English'],
                ]}
              />
              <Button
                size="sm"
                className="h-10"
                onClick={() => {
                  setApplied(search);
                  setPage(1);
                }}
              >
                <Filter className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </div>
          </div>

          {(applied || Object.values(filters).some((v) => v !== 'all')) && (
            <div className="flex items-center gap-2 flex-wrap border-t pt-4">
              <span className="text-xs font-semibold text-muted-foreground mr-1">ACTIVE:</span>
              {applied && <Badge variant="secondary">Search: {applied}</Badge>}
              {Object.entries(filters)
                .filter(([, v]) => v !== 'all')
                .map(([k, v]) => (
                  <Badge key={k} variant="secondary">
                    {k.replace('_', ' ')}: {v}
                  </Badge>
                ))}
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => {
                  setSearch('');
                  setApplied('');
                  setFilters({
                    resolution_state: 'all',
                    feedback: 'all',
                    channel: 'all',
                    language: 'all',
                  });
                  setPage(1);
                }}
              >
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading && !data ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading sessions…
          </div>
        ) : !data?.sessions.length ? (
          <div className="py-20 text-center">
            <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No sessions found</p>
            <p className="text-sm text-muted-foreground mt-1">
              Conversations appear here once users start talking to this agent.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow>
                <TableHead>Session</TableHead>
                <TableHead>First message</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Lang</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>When</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.sessions.map((session) => (
                <TableRow
                  key={session.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/dashboard/agent/chat-logs/${session.id}`)}
                >
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    #{session.id.slice(0, 8)}
                  </TableCell>
                  <TableCell className="max-w-[260px]">
                    <span
                      className="text-sm truncate block italic text-muted-foreground"
                      dir={session.language === 'ar' ? 'rtl' : 'ltr'}
                    >
                      {session.first_message ? `"${session.first_message}"` : '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1.5 text-sm">
                      {session.channel === 'voice' ? (
                        <Mic className="w-3.5 h-3.5 text-brand-blue" />
                      ) : (
                        <MessageSquare className="w-3.5 h-3.5 text-emerald-500" />
                      )}
                      <span className="capitalize">{session.channel}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-xs uppercase text-muted-foreground">
                    {session.language || '—'}
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {session.ai_confidence == null ? '—' : `${session.ai_confidence}%`}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <ResolutionBadge state={session.resolution_state} />
                      <FeedbackBadge feedback={session.feedback} />
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatWhen(session.created_at)}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => navigate(`/dashboard/agent/chat-logs/${session.id}`)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Showing{' '}
            <span className="font-medium text-foreground">
              {(data.page - 1) * data.page_size + 1}–
              {Math.min(data.page * data.page_size, data.total)}
            </span>{' '}
            of <span className="font-medium text-foreground">{data.total.toLocaleString()}</span>
          </p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Per page:</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 text-sm">
                {data.page} / {data.pages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= data.pages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Distribution */}
      {chartData.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-sm font-medium mb-4">Session Distribution</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-center">
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {chartData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-4">
                {chartData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    <span className="text-sm font-bold">{item.value.toLocaleString()}</span>
                  </div>
                ))}
                <Separator />
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm font-bold">Total</span>
                  <span className="text-sm font-bold">
                    {stats.total_sessions.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const StatCard = ({ label, value, percent, sub, icon: Icon, tone }) => (
  <Card className="relative overflow-hidden group">
    <CardContent className="p-6 flex items-center justify-between">
      <div className="space-y-1 min-w-0">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {label}
        </p>
        <div className="flex items-baseline gap-2">
          <h3 className="text-2xl font-bold">{value}</h3>
          {percent && <span className={`${tone} text-sm font-semibold`}>({percent})</span>}
        </div>
        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
      </div>
      <div className={`p-3 rounded-xl bg-muted/50 group-hover:bg-muted transition-colors ${tone}`}>
        <Icon className="w-6 h-6" />
      </div>
    </CardContent>
  </Card>
);

const FilterSelect = ({ value, onChange, options, width }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${width} h-10`}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map(([v, label]) => (
        <SelectItem key={v} value={v}>
          {label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default ChatLogsPage;
